import React, { useState, useEffect, useCallback } from'react';
import { useNavigateBack } from '@/utils/navigationBack';
import { useTranslation, useLanguage } from'@/store/languageStore';
import api from'@/services/api/client';
import OrgTreeSelect from'@/components/common/OrgTreeSelect';
import {
ChartBarSquareIcon,
ArrowDownTrayIcon,
ArrowTrendingUpIcon,
ArrowTrendingDownIcon,
MinusIcon,
ChevronLeftIcon,
} from'@heroicons/react/24/outline';
import { DataTable, ReportPageLayout, LoadingState, SummaryCard } from'@/components/common';
type TabId ='overview' |'vehicleYear' |'vehicleMonth';
interface TrendValues {
distanceKm: number;
fuelL: number;
fuelPer100km: number;
co2Kg: number;
}
interface VehicleKpiData {
vehicleId: string;
registrationNumber: string;
make: string;
model: string;
organizationName: string;
fuelType: string;
status: string;
distanceKm: number;
fuelL: number;
fuelPer100km: number;
co2Kg: number;
/** Distansviktad elandel (%) när data finns */
electricSharePct?: number | null;
durationFormatted: string;
durationSec: number;
daysDriven: number;
kmPerDay: number;
idlingFormatted: string;
idlingSec: number;
avgSpeed: number;
trend: TrendValues | null;
}
interface MonthKpiData {
period: string;
monthNum: number;
distanceKm: number;
fuelL: number;
fuelPer100km: number;
co2Kg: number;
electricSharePct?: number | null;
durationFormatted: string;
durationSec: number;
daysDriven: number;
kmPerDay: number;
idlingFormatted: string;
idlingSec: number;
avgSpeed: number;
trend: TrendValues | null;
}
interface DayKpiData {
date: string;
distanceKm: number;
fuelL: number;
fuelPer100km: number;
co2Kg: number;
electricSharePct?: number | null;
durationFormatted: string;
durationSec: number;
daysDriven: number;
kmPerDay: number;
idlingFormatted: string;
idlingSec: number;
avgSpeed: number;
}
interface OverviewSummary {
totalVehicles: number;
totalDistanceKm: number;
totalFuelL: number;
avgFuelPer100km: number;
avgElectricSharePct?: number | null;
totalCo2Kg: number;
totalDurationFormatted: string;
totalIdlingFormatted: string;
}
interface VehicleInfo {
vehicleId: string;
registrationNumber: string;
make: string;
model: string;
organizationName: string;
fuelType: string;
}
const MONTH_NAMES_SV = ['','Januari','Februari','Mars','April','Maj','Juni','Juli','Augusti','September','Oktober','November','December'];
const MONTH_NAMES_EN = ['','January','February','March','April','May','June','July','August','September','October','November','December'];
const TrendArrow: React.FC<{ value: number; invertColor?: boolean }> = ({ value, invertColor }) => {
if (value === 0) return <MinusIcon className="h-4 w-4 text-textTertiary inline"/>;
const isPositive = invertColor ? value < 0 : value > 0;
if (value > 0) {
return <ArrowTrendingUpIcon className={`h-4 w-4 inline ${isPositive ?'text-green-500' :'text-red-500'}`} />;
}
return <ArrowTrendingDownIcon className={`h-4 w-4 inline ${isPositive ?'text-green-500' :'text-red-500'}`} />;
};
const StatusBadge: React.FC<{ status: string; t: (key: string) => string }> = ({ status, t }) => {
  /** MariaDB/enum skickar ofta ACTIVE; i18n-nycklar är gemener (active, inactive, sold). */
  const normalized = (status || 'inactive').toLowerCase();
  const colors: Record<string, string> = {
    active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    inactive: 'bg-bgBase text-textSecondary',
    sold: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
  };
  const key = `reports.vehicleKpi.status.${normalized}`;
  const translated = t(key);
  const label =
    translated === key || translated.startsWith('reports.vehicleKpi.status.')
      ? status || normalized
      : translated;
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[normalized] || colors.inactive}`}
    >
      {label}
    </span>
  );
};
const VehicleKpiReport: React.FC = () => {
const { t } = useTranslation();
const { language } = useLanguage();
const goBack = useNavigateBack();
const monthNames = language ==='sv' ? MONTH_NAMES_SV : MONTH_NAMES_EN;
const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;
const [activeTab, setActiveTab] = useState<TabId>('overview');
const [loading, setLoading] = useState(false);
const [selectedOrg, setSelectedOrg] = useState('');
const [selectedYear, setSelectedYear] = useState(currentYear);
const [selectedVehicle, setSelectedVehicle] = useState<VehicleInfo | null>(null);
const [selectedMonth, setSelectedMonth] = useState(currentMonth);
const [overviewSummary, setOverviewSummary] = useState<OverviewSummary | null>(null);
const [overviewVehicles, setOverviewVehicles] = useState<VehicleKpiData[]>([]);
const [yearSummary, setYearSummary] = useState<any>(null);
const [yearMonths, setYearMonths] = useState<MonthKpiData[]>([]);
const [monthSummary, setMonthSummary] = useState<any>(null);
const [monthDays, setMonthDays] = useState<DayKpiData[]>([]);
const fetchOverview = useCallback(async () => {
if (!selectedOrg) return;
setLoading(true);
try {
const res = await api.get('/api/reports/vehicle-kpi/overview', {
params: { organizationId: selectedOrg, year: selectedYear },
});
if (res.data?.success) {
setOverviewSummary(res.data.data.summary);
setOverviewVehicles(res.data.data.vehicles);
}
} catch { /* handled silently */ } finally {
setLoading(false);
}
}, [selectedOrg, selectedYear]);
const fetchVehicleYear = useCallback(async () => {
if (!selectedVehicle) return;
setLoading(true);
try {
const res = await api.get('/api/reports/vehicle-kpi/vehicle-year', {
params: { vehicleId: selectedVehicle.vehicleId, year: selectedYear },
});
if (res.data?.success) {
setYearSummary(res.data.data.summary);
setYearMonths(res.data.data.months);
}
} catch { /* handled silently */ } finally {
setLoading(false);
}
}, [selectedVehicle, selectedYear]);
const fetchVehicleMonth = useCallback(async () => {
if (!selectedVehicle) return;
setLoading(true);
try {
const res = await api.get('/api/reports/vehicle-kpi/vehicle-month', {
params: { vehicleId: selectedVehicle.vehicleId, year: selectedYear, month: selectedMonth },
});
if (res.data?.success) {
setMonthSummary(res.data.data.summary);
setMonthDays(res.data.data.days);
}
} catch { /* handled silently */ } finally {
setLoading(false);
}
}, [selectedVehicle, selectedYear, selectedMonth]);
const needsVehiclePicker =
activeTab ==='overview' ||
((activeTab ==='vehicleYear' || activeTab ==='vehicleMonth') && !selectedVehicle);
useEffect(() => {
if (needsVehiclePicker) fetchOverview();
}, [needsVehiclePicker, fetchOverview]);
useEffect(() => {
if (activeTab ==='vehicleYear' && selectedVehicle) fetchVehicleYear();
}, [activeTab, selectedVehicle, fetchVehicleYear]);
useEffect(() => {
if (activeTab ==='vehicleMonth' && selectedVehicle) fetchVehicleMonth();
}, [activeTab, selectedVehicle, fetchVehicleMonth]);
const selectVehicle = (vehicle: VehicleKpiData) => {
setSelectedVehicle({
vehicleId: vehicle.vehicleId,
registrationNumber: vehicle.registrationNumber,
make: vehicle.make,
model: vehicle.model,
organizationName: vehicle.organizationName,
fuelType: vehicle.fuelType,
});
};
const handleVehicleClick = (vehicle: VehicleKpiData) => {
selectVehicle(vehicle);
setActiveTab('vehicleYear');
};
const handleVehicleClickForMonth = (vehicle: VehicleKpiData) => {
selectVehicle(vehicle);
};
const handleMonthClick = (month: MonthKpiData) => {
setSelectedMonth(month.monthNum);
setActiveTab('vehicleMonth');
};
/** Ett steg bakåt i drill-down (månad → år → översikt → föregående sida). */
const handleDrillBack = () => {
if (activeTab ==='vehicleMonth' && selectedVehicle) {
setActiveTab('vehicleYear');
return;
}
if (activeTab ==='vehicleYear' && selectedVehicle) {
setSelectedVehicle(null);
setActiveTab('overview');
return;
}
if (activeTab !=='overview') {
setActiveTab('overview');
return;
}
goBack();
};
const handleExport = async () => {
try {
const response = await api.get('/api/reports/vehicle-kpi/export', {
params: { organizationId: selectedOrg, year: selectedYear, format:'csv' },
responseType:'blob',
});
const url = window.URL.createObjectURL(new Blob([response.data]));
const link = document.createElement('a');
link.href = url;
link.setAttribute('download', `nyckeltal_fordon_${selectedYear}.csv`);
document.body.appendChild(link);
link.click();
link.remove();
} catch { /* handled silently */ }
};
const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);
const tabs: { id: TabId; label: string }[] = [
{ id:'overview', label: t('reports.vehicleKpi.tabs.overview') ||'Organisationsöversikt' },
{ id:'vehicleYear', label: t('reports.vehicleKpi.tabs.vehicleYear') ||'Årsrapport/fordon' },
{ id:'vehicleMonth', label: t('reports.vehicleKpi.tabs.vehicleMonth') ||'Fordon/månad' },
];
const renderBreadcrumb = () => {
if (activeTab ==='overview' || !selectedVehicle) return null;
return (
<div className="flex items-center gap-2 mb-4 text-sm text-textSecondary">
{activeTab ==='vehicleYear' && (
<button
onClick={() => {
setSelectedVehicle(null);
setActiveTab('overview');
}}
className="flex items-center gap-1 hover:text-textDefault transition-colors"
>
<ChevronLeftIcon className="h-4 w-4"/>
{t('reports.vehicleKpi.breadcrumb.backToOverview') ||'Tillbaka till översikt'}
</button>
)}
{activeTab ==='vehicleMonth' && (
<>
<button
type="button"
onClick={() => setActiveTab('vehicleYear')}
className="flex items-center gap-1 hover:text-textDefault transition-colors"
>
<ChevronLeftIcon className="h-4 w-4"/>
{t('reports.vehicleKpi.breadcrumb.backToYear') ||'Tillbaka till årsrapport'}
</button>
<span className="text-textTertiary">/</span>
<button
type="button"
onClick={() => {
setSelectedVehicle(null);
setActiveTab('overview');
}}
className="hover:text-textDefault transition-colors"
>
{t('reports.vehicleKpi.breadcrumb.overview') ||'Översikt'}
</button>
</>
)}
<span className="ml-2 font-medium text-textDefault">
{selectedVehicle.registrationNumber} - {selectedVehicle.organizationName}
</span>
</div>
);
};
const renderFilters = () => (
<div className="grid grid-cols-1 md:grid-cols-4 gap-4">
{(activeTab ==='overview' || !selectedVehicle) && (
<div>
<label className="block text-sm font-medium text-textSecondary mb-1">
{t('reports.vehicleKpi.filters.organization') ||'Organisation'}
</label>
<OrgTreeSelect value={selectedOrg} onChange={setSelectedOrg} showEmptyOption={false} />
</div>
)}
<div>
<label className="block text-sm font-medium text-textSecondary mb-1">
{t('reports.vehicleKpi.filters.year') ||'År'}
</label>
<select
value={selectedYear}
onChange={(e) => setSelectedYear(Number(e.target.value))}
className="w-full px-3 py-2 bg-bgCard border border-border rounded-lg text-textDefault focus:ring-2 focus:ring-bgPrimary"
>
{yearOptions.map((y) => (
<option key={y} value={y}>{y}</option>
))}
</select>
</div>
{activeTab ==='vehicleMonth' && (
<div>
<label className="block text-sm font-medium text-textSecondary mb-1">
{t('reports.vehicleKpi.filters.month') ||'Månad'}
</label>
<select
value={selectedMonth}
onChange={(e) => setSelectedMonth(Number(e.target.value))}
className="w-full px-3 py-2 bg-bgCard border border-border rounded-lg text-textDefault focus:ring-2 focus:ring-bgPrimary"
>
{Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
<option key={m} value={m}>{monthNames[m]}</option>
))}
</select>
</div>
)}
</div>
);
const formatAvgElectricShare = (pct: number | null | undefined) =>
pct != null && Number.isFinite(Number(pct)) ? `${Math.round(Number(pct))} %` :'–';
const renderOverviewSummary = () => {
if (!overviewSummary) return null;
return (
<div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
<SummaryCard
title={t('reports.vehicleKpi.summary.totalVehicles') ||'Antal fordon'}
value={String(overviewSummary.totalVehicles)}
/>
<SummaryCard
title={t('reports.vehicleKpi.summary.totalDistance') ||'Total körsträcka'}
value={`${overviewSummary.totalDistanceKm.toLocaleString()} km`}
/>
<SummaryCard
title={t('reports.vehicleKpi.summary.totalFuel') ||'Total bränsle'}
value={`${overviewSummary.totalFuelL.toLocaleString()} L`}
/>
<SummaryCard
title={t('reports.vehicleKpi.summary.avgFuelPer100km') ||'Snitt L/100km'}
value={String(overviewSummary.avgFuelPer100km)}
/>
<SummaryCard
title={t('reports.vehicleKpi.summary.avgElectricShare') ||'Snitt elandel'}
value={formatAvgElectricShare(overviewSummary.avgElectricSharePct)}
/>
<SummaryCard
title={t('reports.vehicleKpi.summary.totalCo2') ||'Total CO₂'}
value={`${overviewSummary.totalCo2Kg.toLocaleString()} kg`}
/>
<SummaryCard
title={t('reports.vehicleKpi.summary.totalDuration') ||'Total körtid'}
value={overviewSummary.totalDurationFormatted}
/>
<SummaryCard
title={t('reports.vehicleKpi.summary.totalIdling') ||'Total tomgångstid'}
value={overviewSummary.totalIdlingFormatted}
/>
</div>
);
};
const renderYearSummary = () => {
if (!yearSummary) return null;
return (
<div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
<SummaryCard
title={t('reports.vehicleKpi.summary.totalDistance') ||'Total körsträcka'}
value={`${yearSummary.totalDistanceKm.toLocaleString()} km`}
/>
<SummaryCard
title={t('reports.vehicleKpi.summary.totalFuel') ||'Total bränsle'}
value={`${yearSummary.totalFuelL.toLocaleString()} L`}
/>
<SummaryCard
title={t('reports.vehicleKpi.summary.avgFuelPer100km') ||'Snitt L/100km'}
value={String(yearSummary.avgFuelPer100km)}
/>
<SummaryCard
title={t('reports.vehicleKpi.summary.avgElectricShare') ||'Snitt elandel'}
value={formatAvgElectricShare(yearSummary.avgElectricSharePct)}
/>
<SummaryCard
title={t('reports.vehicleKpi.summary.totalCo2') ||'Total CO₂'}
value={`${yearSummary.totalCo2Kg.toLocaleString()} kg`}
/>
<SummaryCard
title={t('reports.vehicleKpi.summary.totalDuration') ||'Total körtid'}
value={yearSummary.totalDurationFormatted}
/>
<SummaryCard
title={t('reports.vehicleKpi.summary.totalIdling') ||'Total tomgångstid'}
value={yearSummary.totalIdlingFormatted}
/>
<SummaryCard
title={t('reports.vehicleKpi.summary.daysDriven') ||'Körda dagar'}
value={`${yearMonths.reduce((s, m) => s + m.daysDriven, 0)}`}
/>
</div>
);
};
const renderMonthSummary = () => {
if (!monthSummary) return null;
return (
<div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
<SummaryCard
title={t('reports.vehicleKpi.summary.totalDistance') ||'Total körsträcka'}
value={`${monthSummary.totalDistanceKm.toLocaleString()} km`}
/>
<SummaryCard
title={t('reports.vehicleKpi.summary.totalFuel') ||'Total bränsle'}
value={`${monthSummary.totalFuelL.toLocaleString()} L`}
/>
<SummaryCard
title={t('reports.vehicleKpi.summary.avgFuelPer100km') ||'Snitt L/100km'}
value={String(monthSummary.avgFuelPer100km)}
/>
<SummaryCard
title={t('reports.vehicleKpi.summary.avgElectricShare') ||'Snitt elandel'}
value={formatAvgElectricShare(monthSummary.avgElectricSharePct)}
/>
<SummaryCard
title={t('reports.vehicleKpi.summary.totalCo2') ||'Total CO₂'}
value={`${monthSummary.totalCo2Kg.toLocaleString()} kg`}
/>
<SummaryCard
title={t('reports.vehicleKpi.summary.totalDuration') ||'Total körtid'}
value={monthSummary.totalDurationFormatted}
/>
<SummaryCard
title={t('reports.vehicleKpi.summary.totalIdling') ||'Total tomgångstid'}
value={monthSummary.totalIdlingFormatted}
/>
<SummaryCard
title={t('reports.vehicleKpi.summary.daysDriven') ||'Körda dagar'}
value={String(monthSummary.daysDriven)}
/>
</div>
);
};
const col = (key: string) => t(`reports.vehicleKpi.columns.${key}`) || key;
const overviewColumns = [
{
key:'registrationNumber',
label: col('registrationNumber'),
sortable: true,
render: (_: any, row: VehicleKpiData) => (
<div>
<div className="font-medium text-textDefault">{row.registrationNumber}</div>
<div className="text-xs text-textSecondary">{row.make} {row.model}</div>
</div>
),
csvValue: (_: any, row: VehicleKpiData) => `${row.registrationNumber} ${row.make} ${row.model}`,
},
{ key:'organizationName', label: col('organization'), sortable: true },
{ key:'distanceKm', label: col('distanceKm'), align:'right' as const, sortable: true, render: (v: any) => Number(v).toLocaleString() },
{ key:'fuelL', label: col('fuelL'), align:'right' as const, sortable: true, render: (v: any) => Number(v).toLocaleString() },
{ key:'fuelPer100km', label: col('fuelPer100km'), align:'right' as const, sortable: true },
{
key:'electricSharePct',
label: col('electricSharePct'),
align:'right' as const,
sortable: true,
render: (v: any) => (v != null && Number.isFinite(Number(v)) ? `${Math.round(Number(v))} %` : '–'),
},
{ key:'co2Kg', label: col('co2Kg'), align:'right' as const, sortable: true, render: (v: any) => Number(v).toLocaleString() },
{ key:'daysDriven', label: col('daysDriven'), align:'right' as const, sortable: true },
{ key:'kmPerDay', label: col('kmPerDay'), align:'right' as const, sortable: true },
{ key:'durationFormatted', label: col('duration'), align:'right' as const, sortable: true, sortKey:'durationSec' },
{ key:'avgSpeed', label: col('avgSpeed'), align:'right' as const, sortable: true, render: (v: any) => v > 0 ? `${v} km/h` :'-' },
{ key:'idlingFormatted', label: col('idling'), align:'right' as const, sortable: true, sortKey:'idlingSec' },
{
key:'trend',
label: col('trend'),
align:'center' as const,
render: (_: any, row: VehicleKpiData) => {
if (!row.trend) return'-';
return (
<div className="flex items-center gap-1 justify-center">
<TrendArrow value={row.trend.distanceKm} />
<TrendArrow value={row.trend.fuelPer100km} invertColor />
<TrendArrow value={row.trend.co2Kg} invertColor />
</div>
);
},
csvValue: () =>'',
},
{
key:'status',
label: col('status'),
align:'center' as const,
sortable: true,
render: (_: any, row: VehicleKpiData) => <StatusBadge status={row.status} t={t} />,
},
];
const yearColumns = [
{
key:'period',
label: col('month'),
sortable: true,
render: (_: any, row: MonthKpiData) => monthNames[row.monthNum] || row.period,
},
{ key:'distanceKm', label: col('distanceKm'), align:'right' as const, sortable: true, render: (v: any) => Number(v).toLocaleString() },
{ key:'fuelL', label: col('fuelL'), align:'right' as const, sortable: true, render: (v: any) => Number(v).toLocaleString() },
{ key:'fuelPer100km', label: col('fuelPer100km'), align:'right' as const, sortable: true },
{
key:'electricSharePct',
label: col('electricSharePct'),
align:'right' as const,
sortable: true,
render: (v: any) => (v != null && Number.isFinite(Number(v)) ? `${Math.round(Number(v))} %` : '–'),
},
{ key:'co2Kg', label: col('co2Kg'), align:'right' as const, sortable: true, render: (v: any) => Number(v).toLocaleString() },
{ key:'daysDriven', label: col('daysDriven'), align:'right' as const, sortable: true },
{ key:'kmPerDay', label: col('kmPerDay'), align:'right' as const, sortable: true },
{ key:'durationFormatted', label: col('duration'), align:'right' as const, sortable: true, sortKey:'durationSec' },
{ key:'avgSpeed', label: col('avgSpeed'), align:'right' as const, sortable: true, render: (v: any) => v > 0 ? `${v} km/h` :'-' },
{ key:'idlingFormatted', label: col('idling'), align:'right' as const, sortable: true, sortKey:'idlingSec' },
{
key:'trend',
label: col('trend'),
align:'center' as const,
render: (_: any, row: MonthKpiData) => {
if (!row.trend) return'-';
return (
<div className="flex items-center gap-1 justify-center">
<TrendArrow value={row.trend.distanceKm} />
<TrendArrow value={row.trend.fuelPer100km} invertColor />
<TrendArrow value={row.trend.co2Kg} invertColor />
</div>
);
},
csvValue: () =>'',
},
];
const dayColumns = [
{ key:'date', label: col('date'), sortable: true },
{ key:'distanceKm', label: col('distanceKm'), align:'right' as const, sortable: true, render: (v: any) => Number(v).toLocaleString() },
{ key:'fuelL', label: col('fuelL'), align:'right' as const, sortable: true, render: (v: any) => Number(v).toLocaleString() },
{ key:'fuelPer100km', label: col('fuelPer100km'), align:'right' as const, sortable: true },
{
key:'electricSharePct',
label: col('electricSharePct'),
align:'right' as const,
sortable: true,
render: (v: any) => (v != null && Number.isFinite(Number(v)) ? `${Math.round(Number(v))} %` : '–'),
},
{ key:'co2Kg', label: col('co2Kg'), align:'right' as const, sortable: true, render: (v: any) => Number(v).toLocaleString() },
{ key:'durationFormatted', label: col('duration'), align:'right' as const, sortable: true, sortKey:'durationSec' },
{ key:'avgSpeed', label: col('avgSpeed'), align:'right' as const, sortable: true, render: (v: any) => v > 0 ? `${v} km/h` :'-' },
{ key:'idlingFormatted', label: col('idling'), align:'right' as const, sortable: true, sortKey:'idlingSec' },
];
return (
<ReportPageLayout
title={t('reports.vehicleKpi.title') ||'Nyckeltal fordon'}
description={t('reports.vehicleKpi.description') ||'Sammanställning av nyckeltal per fordon, månad och år'}
icon={<ChartBarSquareIcon className="w-6 h-6 text-primary"/>}
onBack={handleDrillBack}
actions={
needsVehiclePicker ? (
<button
onClick={handleExport}
className="flex items-center gap-2 px-4 py-2 bg-bgPrimary text-textLight rounded-lg hover:bg-bgSecondary"
>
<ArrowDownTrayIcon className="h-5 w-5"/>
{t('reports.vehicleKpi.export') ||'Exportera CSV'}
</button>
) : undefined
}
filters={renderFilters()}
>
{/* Tabs */}
<div className="flex border-b border-border mb-4">
{tabs.map((tab) => (
<button
key={tab.id}
onClick={() => setActiveTab(tab.id)}
className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
activeTab === tab.id
?'border-bgPrimary text-bgPrimary'
:'border-transparent text-textSecondary hover:text-textDefault'
}`}
>
{tab.label}
</button>
))}
</div>
{renderBreadcrumb()}
{loading ? (
<LoadingState />
) : (
<>
{/* Overview Tab */}
{activeTab ==='overview' && (
<>
{renderOverviewSummary()}
<div className="bg-bgCard rounded-lg shadow-sm overflow-hidden mt-4">
<DataTable<VehicleKpiData>
data={overviewVehicles}
rowKey="vehicleId"
pageSize={25}
emptyMessage={
!selectedOrg
? (t('reports.vehicleKpi.selectOrganization') ||'Välj organisation för att visa fordon')
: (t('reports.vehicleKpi.noData') ||'Ingen data hittades för vald period')
}
columns={overviewColumns}
features={{ csvExport: true, search: true, stickyHeader: true }}
onRowClick={handleVehicleClick}
/>
</div>
</>
)}
{/* Vehicle Year Tab */}
{activeTab ==='vehicleYear' && (
<>
{!selectedVehicle ? (
<>
<p className="mb-3 text-sm text-textSecondary">
{t('reports.vehicleKpi.selectVehicleForYear') ||
'Årssummering per fordon. Klicka på ett fordon för månadsfördelning.'}
</p>
{renderOverviewSummary()}
<div className="bg-bgCard rounded-lg shadow-sm overflow-hidden mt-4">
<DataTable<VehicleKpiData>
data={overviewVehicles}
rowKey="vehicleId"
pageSize={25}
emptyMessage={
!selectedOrg
? (t('reports.vehicleKpi.selectOrganization') ||'Välj organisation för att visa fordon')
: (t('reports.vehicleKpi.noData') ||'Ingen data hittades för vald period')
}
columns={overviewColumns}
features={{ csvExport: true, search: true, stickyHeader: true }}
onRowClick={handleVehicleClick}
/>
</div>
</>
) : (
<>
<div className="mb-4 p-3 bg-bgElevated rounded-lg border border-border">
<span className="font-medium text-textDefault">
{selectedVehicle.registrationNumber}
</span>
<span className="text-textSecondary ml-2">
{selectedVehicle.make} {selectedVehicle.model} &middot; {selectedVehicle.organizationName}
</span>
</div>
{renderYearSummary()}
<div className="bg-bgCard rounded-lg shadow-sm overflow-hidden mt-4">
<DataTable<MonthKpiData>
data={yearMonths}
rowKey="period"
pageSize={12}
emptyMessage={t('reports.vehicleKpi.noData') ||'Ingen data hittades för vald period'}
columns={yearColumns}
features={{ csvExport: true, stickyHeader: true }}
onRowClick={handleMonthClick}
/>
</div>
</>
)}
</>
)}
{/* Vehicle Month Tab */}
{activeTab ==='vehicleMonth' && (
<>
{!selectedVehicle ? (
<>
<p className="mb-3 text-sm text-textSecondary">
{t('reports.vehicleKpi.selectVehicleForMonth') ||
'Välj fordon i listan för att se dagsfördelning för vald månad.'}
</p>
{renderOverviewSummary()}
<div className="bg-bgCard rounded-lg shadow-sm overflow-hidden mt-4">
<DataTable<VehicleKpiData>
data={overviewVehicles}
rowKey="vehicleId"
pageSize={25}
emptyMessage={
!selectedOrg
? (t('reports.vehicleKpi.selectOrganization') ||'Välj organisation för att visa fordon')
: (t('reports.vehicleKpi.noData') ||'Ingen data hittades för vald period')
}
columns={overviewColumns}
features={{ csvExport: true, search: true, stickyHeader: true }}
onRowClick={handleVehicleClickForMonth}
/>
</div>
</>
) : (
<>
<div className="mb-4 p-3 bg-bgElevated rounded-lg border border-border">
<span className="font-medium text-textDefault">
{selectedVehicle.registrationNumber}
</span>
<span className="text-textSecondary ml-2">
{selectedVehicle.make} {selectedVehicle.model} &middot; {selectedVehicle.organizationName}
&middot; {monthNames[selectedMonth]} {selectedYear}
</span>
</div>
{renderMonthSummary()}
<div className="bg-bgCard rounded-lg shadow-sm overflow-hidden mt-4">
<DataTable<DayKpiData>
data={monthDays}
rowKey="date"
pageSize={31}
emptyMessage={t('reports.vehicleKpi.noData') ||'Ingen data hittades för vald period'}
columns={dayColumns}
features={{ csvExport: true, stickyHeader: true }}
/>
</div>
</>
)}
</>
)}
</>
)}
</ReportPageLayout>
);
};
export default VehicleKpiReport;
